import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Layout from '../components/Layout'
import ConfirmDialog from '../components/ConfirmDialog'
import { useWallet } from '../contexts/WalletContext'
import { useToast } from '../contexts/ToastContext'
import { formatCHNX } from '../utils/formatCHNX'

interface Duel {
  duelId: string
  matchId: string
  matchTitle: string
  creatorAddress: string
  opponentAddress: string | null
  creatorSide: 'sideA' | 'sideB'
  opponentSide: 'sideA' | 'sideB' | null
  creatorAmount: string
  opponentAmount: string | null
  totalPool: string
  status: string
  result: string | null
  createdAt: string
  matchedAt: string | null
  settledAt: string | null
}

interface DuelsByStatus {
  open: Duel[]
  active: Duel[]
  completed: Duel[]
  cancelled: Duel[]
}

export default function MyDuels() {
  const { address: walletAddress, isConnected, refreshBalance } = useWallet()
  const toast = useToast()
  const [duels, setDuels] = useState<DuelsByStatus>({
    open: [],
    active: [],
    completed: [],
    cancelled: [],
  })
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'open' | 'active' | 'completed' | 'cancelled'>('active')
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [duelToCancel, setDuelToCancel] = useState<string | null>(null)

  useEffect(() => {
    if (isConnected && walletAddress) {
      fetchMyDuels(walletAddress)
    }
  }, [isConnected, walletAddress])

  const fetchMyDuels = async (address: string) => {
    try {
      const response = await fetch(`/api/duel/my-duels?address=${address}`)
      const data = await response.json()
      if (data.success) {
        setDuels(data.data)
      }
    } catch (error) {
      console.error('Failed to fetch my duels:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCancelDuel = async (duelId: string) => {
    setDuelToCancel(duelId)
    setCancelDialogOpen(true)
  }

  const confirmCancelDuel = async () => {
    if (!duelToCancel) return

    try {
      toast.info('Cancelling prediction and processing refund...')
      
      const response = await fetch(`/api/duel/${duelToCancel}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ walletAddress }),
      })
      
      const data = await response.json()
      
      if (data.success) {
        const { originalAmount, refundAmount, cancellationFee } = data.data
        toast.success(
          `Prediction cancelled!\n` +
          `Original: ${originalAmount} CHNX\n` +
          `Fee (5%): ${cancellationFee} CHNX\n` +
          `Refunded: ${refundAmount} CHNX`
        )
        
        // Refresh duels list
        if (walletAddress) {
          fetchMyDuels(walletAddress)
        }
        
        // Refresh wallet balance
        refreshBalance()
      } else {
        toast.error(`Failed to cancel prediction: ${data.error}`)
      }
    } catch (error) {
      console.error('Failed to cancel duel:', error)
      toast.error('Network error, please try again')
    } finally {
      setDuelToCancel(null)
    }
  }

  const getStatusBadge = (status: string) => {
    const styles = {
      open: 'bg-yellow-100 text-yellow-800',
      active: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800',
    }
    return styles[status as keyof typeof styles] || styles.cancelled
  }

  const getResultBadge = (result: string | null) => {
    if (!result) return null
    
    const styles = {
      won: 'bg-green-500 text-white',
      lost: 'bg-red-500 text-white',
      draw: 'bg-gray-500 text-white',
    }
    
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${styles[result as keyof typeof styles]}`}>
        {result.toUpperCase()}
      </span>
    )
  }

  const renderDuelCard = (duel: Duel) => {
    const isCreator = duel.creatorAddress.toLowerCase() === (walletAddress?.toLowerCase() || '')
    const opponentAddr = isCreator ? duel.opponentAddress : duel.creatorAddress
    const mySide = isCreator ? duel.creatorSide : duel.opponentSide
    const myAmount = isCreator ? duel.creatorAmount : duel.opponentAmount

    return (
      <div key={duel.duelId} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
        <div className="flex justify-between items-start mb-4">
          <div>
            <Link
              to={`/match/${duel.matchId}`}
              className="text-lg font-semibold text-gray-900 hover:text-indigo-600"
            >
              {duel.matchTitle || 'Match Details →'}
            </Link>
            <p className="text-sm text-gray-500 mt-1">
              Duel ID: {duel.duelId.slice(0, 12)}...
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(duel.status)}`}>
              {duel.status}
            </span>
            {duel.result && getResultBadge(duel.result)}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
          <div>
            <p className="text-xs text-gray-600 mb-1">Your Side</p>
            <p className="font-semibold text-gray-900">{mySide === 'sideA' ? 'Side A' : 'Side B'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-600 mb-1">Your Bet</p>
            <p className="font-semibold text-gray-900">{formatCHNX(myAmount || '0')} CHNX</p>
          </div>
        </div>

        {duel.status === 'active' && opponentAddr && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-gray-600 mb-1">Opponent</p>
            <p className="font-mono text-xs text-gray-900">
              {opponentAddr.slice(0, 10)}...{opponentAddr.slice(-8)}
            </p>
            <p className="text-xs text-gray-600 mt-2">Total Pool: {parseFloat(duel.totalPool).toFixed(3)} CHNX</p>
          </div>
        )}

        <div className="text-xs text-gray-500 space-y-1">
          <p>Created: {new Date(duel.createdAt).toLocaleString()}</p>
          {duel.matchedAt && <p>Matched: {new Date(duel.matchedAt).toLocaleString()}</p>}
          {duel.settledAt && <p>Completed: {new Date(duel.settledAt).toLocaleString()}</p>}
        </div>

        {duel.status === 'open' && (
          <button
            onClick={() => handleCancelDuel(duel.duelId)}
            className="mt-4 w-full px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium"
          >
            Cancel Duel
          </button>
        )}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Loading your duels...</div>
      </div>
    )
  }

  const tabs = [
    { key: 'active' as const, label: 'Active', count: duels.active.length },
    { key: 'open' as const, label: 'Open', count: duels.open.length },
    { key: 'completed' as const, label: 'Completed', count: duels.completed.length },
    { key: 'cancelled' as const, label: 'Cancelled', count: duels.cancelled.length },
  ]

  const currentDuels = duels[activeTab]

  return (
    <Layout>
      <div>
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900">My Duels</h2>
        <p className="mt-2 text-gray-600">
          Track and manage all your duels
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.key
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </nav>
      </div>

      {/* Duels List */}
      {currentDuels.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500 text-lg">No {activeTab} duels</p>
          <p className="text-gray-400 text-sm mt-2">
            {activeTab === 'open' && 'Create a new duel to get started'}
            {activeTab === 'active' && 'Your active duels will appear here'}
            {activeTab === 'completed' && 'Completed duels will show here'}
            {activeTab === 'cancelled' && 'Cancelled duels will be listed here'}
          </p>
          {activeTab === 'open' && (
            <Link
              to="/"
              className="mt-4 inline-block px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Browse Matches
            </Link>
          )}
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          {currentDuels.map(renderDuelCard)}
        </div>
      )}
      </div>

      {/* Cancel Confirmation Dialog */}
      <ConfirmDialog
        isOpen={cancelDialogOpen}
        onClose={() => {
          setCancelDialogOpen(false)
          setDuelToCancel(null)
        }}
        onConfirm={confirmCancelDuel}
        title="Cancel Prediction"
        message={`Are you sure you want to cancel this prediction?\n\n⚠️ A 5% cancellation fee will be deducted.\n💰 95% of your stake will be refunded.`}
        confirmText="Yes, Cancel"
        cancelText="No, Keep It"
        type="warning"
      />
    </Layout>
  )
}
